import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import React, { useMemo, useState } from "react";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  IWebsiteBuilderImageSettings,
  TWebsiteBuilderImageObjectFit,
  WEBSITE_BUILDER_IMAGE_MIN_BORDER_RADIUS,
  WEBSITE_BUILDER_IMAGE_MAX_BORDER_RADIUS,
  WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MIN,
  WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MAX,
  WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MIN,
  WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MAX,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderImage";
import { Select } from "components/select/index";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import Input from "components/form/input/index";
import Toggle from "components/form/toggle/Toggle";
import { ButtonNew as Button } from "components/buttonNew/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";
import { IWebsiteBuilderMediaItem } from "redux/reducers/models/websiteBuilder";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import { WebsiteBuilderSelectMedia } from "../../../../../helpers/WebsiteBuilderSelectMedia";
import { WebsiteBuilderHoverSettings } from "../../../../../helpers/WebsiteBuilderHoverSettings";
import { CustomColorPickerPopover } from "components/colorpicker";

interface IImageSettingsProps {
  id: string;
  settings: IWebsiteBuilderImageSettings;
  closeSettings: () => void;
}

export function ImageSettings(props: IImageSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderImageSettings>(() => cloneDeep(settings));

  const { state: selectImageModalState, updateModal: updateSelectImageModal, closeModal: closeSelectImageModal } = useModal();

  const objectFitOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderImageObjectFit>[] = useMemo(() => {
    return [
      {
        label: t("Cover"),
        value: "cover",
      },
      {
        label: t("Contain"),
        value: "contain",
      },
      {
        label: t("Fill"),
        value: "fill",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderImageSettings, nextSettings: IWebsiteBuilderImageSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "image",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "image",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleChangeBorderRadius(borderRadius: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      borderRadius,
    }));
  }

  function handleChangeHoverScaleAmount(hoverScaleAmount: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      hoverScaleAmount,
    }));
  }

  function handleChangeHoverOverlayOpacity(hoverOverlayOpacity: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      hoverOverlayOpacity,
    }));
  }

  function handleOpenSelectImageModal() {
    updateSelectImageModal({ isOpen: true });
  }

  function handleCloseSelectImageModal() {
    closeSelectImageModal();
  }

  function handleSelectImage(image: IWebsiteBuilderMediaItem) {
    handleUpdateState(prev => ({
      ...prev,
      mediaId: image.id,
      src: image.source,
    }));

    handleCloseSelectImageModal();
  }
  
  function renderSettings() {
    return (
      <>
        <Select
          label={t("Fit")}
          onChange={(objectFit: TWebsiteBuilderImageObjectFit) =>
            handleUpdateState(prev => ({
              ...prev,
              objectFit,
            }))
          }
          defaultValue={updatedSettings.objectFit}
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(objectFitOptions)}
        </Select>
        <Input
          label={t("Alternate Text")}
          value={updatedSettings.alt}
          onChange={e => {
            handleDebouncedUpdateState(prev => ({
              ...prev,
              alt: e.target.value,
            }));
          }}
        />
        <WebsiteBuilderRange
          label={t("Border radius (px)")}
          min={WEBSITE_BUILDER_IMAGE_MIN_BORDER_RADIUS}
          max={WEBSITE_BUILDER_IMAGE_MAX_BORDER_RADIUS}
          step={1}
          value={updatedSettings.borderRadius}
          setValue={handleChangeBorderRadius}
        />
        <Toggle
          labelRight={t("Expand on click")}
          checked={updatedSettings.expandOnClick}
          onChange={e =>
            handleUpdateState(prev => ({
              ...prev,
              expandOnClick: e.target.checked,
            }))
          }
        />
        <Button
          size="small"
          type="secondary"
          block
          onClick={handleOpenSelectImageModal}
        >
          <FontAwesomeIcon icon={faImage} />&nbsp;{t("Select image")}
        </Button>

        <WebsiteBuilderSelectMedia
          type="image"
          mediaId={updatedSettings.mediaId}
          isOpen={selectImageModalState.isOpen}
          closeModal={handleCloseSelectImageModal}
          handleSelectMedia={handleSelectImage}
        />
        <WebsiteBuilderHoverSettings>
          <Toggle
            labelRight={t("Scale")}
            checked={updatedSettings.hoverScale}
            onChange={e =>
              handleUpdateState(prev => ({
                ...prev,
                hoverScale: e.target.checked,
              }))
            }
          />
          {updatedSettings.hoverScale && (
            <WebsiteBuilderRange
              label={t("Amount (%)")}
              min={WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MIN}
              max={WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MAX}
              step={1}
              value={updatedSettings.hoverScaleAmount}
              setValue={handleChangeHoverScaleAmount}
            />
          )}
          <Toggle
            labelRight={t("Overlay")}
            checked={updatedSettings.hoverOverlay}
            onChange={e => {
              const hoverOverlay = e.target.checked;

              handleUpdateState(prev => ({
                ...prev,
                hoverOverlay,
                hoverOverlayColor: hoverOverlay && prev.hoverOverlayColor == null ? "#000000" : prev.hoverOverlayColor,
              }));
            }}
          />
          {updatedSettings.hoverOverlay && (
            <>
              <CustomColorPickerPopover
                title={t("Color")}
                hexColor={updatedSettings.hoverOverlayColor}
                updateHexColor={hoverOverlayColor =>
                  handleDebouncedUpdateState(prev => ({
                    ...prev,
                    hoverOverlayColor,
                  }))
                }
                portalParentId={WEBSITE_BUILDER_PORTAL}
              />
              <WebsiteBuilderRange
                label={t("Opacity (%)")}
                min={WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MIN}
                max={WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MAX}
                step={5}
                value={updatedSettings.hoverOverlayOpacity}
                setValue={handleChangeHoverOverlayOpacity}
              />
            </>
          )}
        </WebsiteBuilderHoverSettings>
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Image settings")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}